import { User } from 'oidc-client-ts';
import { LoginInfo } from './Login';

// Convert OIDC User to LoginInfo format
// Cognito stores all user attributes, so we can map them properly
export function convertOidcUserToLoginInfo(user: User): LoginInfo {
    const profile = user.profile;

    // Helper function to safely get string values from profile
    const getStringValue = (key: string): string => {
        const value = profile[key as keyof typeof profile];
        return typeof value === 'string' ? value : '';
    };

    // Helper function to safely get number values from profile
    const getNumberValue = (key: string): number => {
        const value = profile[key as keyof typeof profile];
        if (typeof value === 'string') {
            const parsed = parseInt(value);
            return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
    };

    return {
        id: getNumberValue('custom:user_id') || parseInt(profile.sub || '0'), // Use custom:user_id if available, fallback to sub
        first_name: getStringValue('given_name') || getStringValue('name') || profile.email?.split('@')[0] || 'User',
        last_name: getStringValue('family_name'),
        year: getNumberValue('custom:year'),
        chapter: getStringValue('custom:chapter'),
        type: getStringValue('custom:user_type') || 'normal', // Default to normal if not specified
        InitiationNumber: getNumberValue('custom:initiation_number'),
        MITAlumniID: getStringValue('custom:mit_alumni_id'),
        Enabled: profile.email_verified || false,
        PledgeClassID: getNumberValue('custom:pledge_class_id'),
        BigBrotherID: getNumberValue('custom:big_brother_id'),
        FamilyID: getNumberValue('custom:family_id'),
    };
}

// Check if user has required permissions based on user type
export function hasPermission(userInfo: LoginInfo | null, requiredTypes: string[]): boolean {
    if (!userInfo || !userInfo.id) return false;
    return requiredTypes.includes(userInfo.type);
}

// Check if user is admin
export function isAdmin(userInfo: LoginInfo | null): boolean {
    return hasPermission(userInfo, ['admin']);
}

// Check if user is normal user or admin
export function isNormalUserOrAdmin(userInfo: LoginInfo | null): boolean {
    return hasPermission(userInfo, ['normal', 'admin']);
}



// Get user display name
export function getUserDisplayName(userInfo: LoginInfo | null): string {
    if (!userInfo) return '';
    return `${userInfo.first_name} ${userInfo.last_name}`.trim() || 'Unknown User';
}

// Get user chapter and year display
export function getUserChapterYear(userInfo: LoginInfo | null): string {
    if (!userInfo) return '';
    const parts = [];
    if (userInfo.chapter) parts.push(userInfo.chapter);
    if (userInfo.year) parts.push(userInfo.year.toString());
    return parts.join(' ');
}
